<template>
  <div class="blogCategoriesNavbar">
    <div class="container blogCategoriesNavbar__container">
      <span class="blogCategoriesNavbar__title">{{ title }}</span>
      <div class="blogCategoriesNavbar__tags">
        <nuxt-link
          v-for="category in categories"
          :key="`tab_${category.id}`"
          :to="
            localePath({
              name: 'blog-category-slug',
              params: { slug: category.slug },
            })
          "
          @click="active = category.id"
          :class="[
            'blogCategoriesNavbar__tag',
            {
              'blogCategoriesNavbar__tag--active':
                category.id === active || category.slug === activeCategory,
            },
          ]"
        >
          {{ category.name }}
        </nuxt-link>
      </div>
      <button class="blogCategoriesNavbar__iconButton" @click="toggleSearch">
        <span class="icon-search blogCategoriesNavbar__icon"></span>
      </button>
    </div>
    <div
      class="blogCategoriesNavbar__search"
      :class="[
        'blogCategoriesNavbar__search',
        { 'blogCategoriesNavbar__search--active': isMenuOpen },
      ]"
    >
      <div class="container">
        <div class="blogCategoriesNavbar__searchContainer">
          <div class="blogCategoriesNavbar__iconSearch" @click="goToSearch">
            <span class="icon-search"></span>
          </div>
          <input
            :ref="'blogCategoriesNavbar__input'"
            :value="query"
            class="blogCategoriesNavbar__input"
            placeholder="Wpisz szukaną frazę..."
            @input="search"
            @keyup.enter="goToSearch"
          />
          <div class="blogCategoriesNavbar__iconClose" @click="toggleSearch">
            <span class="icon-line-close"></span>
          </div>
        </div>
      </div>
    </div>
    <div
      ref="searchDropdown"
      class="headerSearch__dropdown prettyScrollbar container blogCategoriesNavbar__dropdown"
      :class="[
        'blogCategoriesNavbar__dropdown',
        { 'blogCategoriesNavbar__dropdown--active': isMenuOpen },
      ]"
    >
      <div class="headerSearch__dropdownContent">
        <div class="headerSearch__categories">
          <div class="headerSearch__category">
            <ul class="headerSearch__productList">
              <li v-for="article in articles" :key="article.id" class="headerSearch__productItem">
                <nuxt-link
                  class="headerSearch__productLink"
                  :to="localePath({ name: 'blog-slug', params: { slug: article.key } })"
                  @click.native="toggleSearch()"
                >
                  <div class="headerSearch__productImage">
                    <new-image-box
                      :alt="article.title"
                      :src="article.image"
                      :width="60"
                      :height="60"
                      image-params="w_60,h_60"
                    />
                  </div>
                  <div class="headerSearch__productDesc">
                    <div v-line-clamp:20="1" class="headerSearch__productName">
                      {{ article.title }}
                    </div>
                  </div>
                </nuxt-link>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState, mapMutations, mapActions } from 'vuex';
import SliderTiles from '~/components/Sliders/SliderTiles';
import VButton from '~/components/VButton';
import NewImageBox from '@/components/NewImageBox';

const dropdownOptions = {
  maxHeight: 410,
};

export default {
  name: 'BlogCategoriesNavbar',
  components: {
    SliderTiles,
    VButton,
    NewImageBox,
  },
  props: {
    title: {
      type: String,
      default: '',
    },
    categories: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      active: 0,
      isMenuOpen: false,
    };
  },
  computed: {
    ...mapState({
      items: state => state.news.items,
      query: state => state.news.filters.query,
      articles: state => state.news.autocompleteData,
      activeCategory: state => state.news.filters.categorySlug,
    }),
  },
  watch: {
    active: {
      handler() {
        setTimeout(() => {
          this.$nextTick(() => {
            this.scrollActiveTagIntoView();
          });
        }, 100);
      },
      immediate: true,
    },
  },
  methods: {
    ...mapMutations({
      setQuery: 'news/setQuery',
      toggleShowPageDarkLayer: 'layout/toggleShowPageDarkLayer',
    }),
    ...mapActions({
      fetchData: 'news/fetchData',
      fetchAutocomplete: 'news/fetchAutocomplete',
    }),
    scrollActiveTagIntoView() {
      if (typeof document !== 'undefined') {
        const activeTagElement = document.querySelector('.blogCategoriesNavbar__tag--active');

        if (activeTagElement) {
          activeTagElement.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
            inline: 'center',
          });
        }
      }
    },

    toggleSearch() {
      this.isMenuOpen = !this.isMenuOpen;
      this.toggleShowPageDarkLayer(this.isMenuOpen);
    },
    openSearchList() {
      const dropdown = this.$refs.searchDropdown;
      if (!dropdown) return;
      if (window.innerWidth > 768) {
        dropdown.style.maxHeight =
          dropdown.scrollHeight < dropdownOptions.maxHeight
            ? `${dropdown.scrollHeight}px`
            : `${dropdownOptions.maxHeight}px`;
      } else if (dropdown) {
        dropdown.style.maxHeight = `calc((var(--vh, 1vh) * 100) - 48px - 4rem)`;
        const viewport = window.innerHeight * 0.01;
        dropdown.style.setProperty('--vh', `${viewport}px`);
      }
      this.openDropdown = true;
    },
    closeSearchList() {
      const dropdown = this.$refs.searchDropdown;
      if (!dropdown) return;
      dropdown.style.maxHeight = '0px';
      this.openDropdown = false;
    },
    async search(e) {
      try {
        const value = e.target.value;
        this.setQuery(value);
        await this.fetchAutocomplete();
        if (value === '') {
          this.closeSearchList();
        } else {
          this.openSearchList();
        }
      } catch (error) {
        // console.error(error);
      }
    },
    goToSearch() {
      if (this.$route.name.split('___')[0] === 'blog') {
        this.$router.replace(
          this.localePath({
            name: 'blog',
            query: {
              query: this.query,
            },
          }),
        );

        this.fetchData();
      } else {
        this.$router.push(
          this.localePath({
            name: 'blog',
            query: {
              query: this.query,
            },
          }),
        );

        this.closeSearchList();
        this.toggleSearch();
      }
    },
  },
  destroyed() {
    this.toggleShowPageDarkLayer(false);
  },
};
</script>

<style lang="scss" scoped>
.pageWrapper--fixed {
  .blogCategoriesNavbar {
    box-shadow: 0 3px 20px 0 rgba(23, 25, 51, 0.07);
    @include rwd('tablet') {
      position: fixed;
      top: 5.7rem;
    }

    &__searchContainer {
      @include rwd('large-phone') {
        margin: 0;
      }
    }

    &__search {
      top: 5.1rem;

      @include rwd('large-phone') {
        top: 6.7rem;
      }
    }

    &__dropdown {
      &--active {
        @include rwd('tablet') {
          top: 17.5rem;
        }

        @include rwd('large-phone') {
          top: 16rem;
        }
      }
    }
  }
}

.blogCategoriesNavbar {
  position: sticky;
  top: 9.6rem;
  padding: 2rem 0;
  width: 100%;
  background: $cBg;
  z-index: 1;
  border-bottom: 1px solid $cAddLight;
  background: $cWhite;
  transition: padding $tr, box-shadow $tr;

  @include rwd('ultra') {
    top: 8.6rem;
  }

  @include rwd('tablet') {
    top: 5.6rem;
  }

  &__container {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
  }

  &__title {
    font-size: 1.6rem;
    line-height: 1.4rem;
    margin-right: 2rem;
    order: 1;
  }

  &__tags {
    display: flex;
    gap: 0.5rem;
    order: 2;

    @include rwd('large-tablet') {
      order: 3;
      max-width: 100%;
      flex: 1 1 100%;
      margin-top: 1rem;
      justify-content: center;
      overflow: auto;
    }

    @include rwd('tablet') {
      justify-content: flex-start;
      padding-bottom: 1rem;
    }
  }

  &__tag {
    display: flex;
    align-items: center;
    text-align: center;
    padding: 1.5rem 3rem;
    background: $cWhite;
    border: 1px solid $cAddLight;
    border-radius: 10rem;
    font-size: 1.6rem;
    color: $cAddDark02;
    transition: background $tr, color $tr;
    white-space: nowrap;

    &:hover {
      background: $cMainHover;
      color: $cWhite;
    }

    &--active {
      background: $cMain;
      color: $cWhite;
    }
  }

  &__iconButton {
    padding: 1.2rem;
    background: $cWhite;
    border: 1px solid $cAddLight;
    border-radius: 50%;
    justify-self: flex-end;
    margin-left: auto;
    transition: background $tr, color $tr;
    order: 3;

    @include rwd('large-tablet') {
      order: 2;
    }

    &:hover {
      background: $cMainHover;
      color: $cWhite;
    }
  }

  &__icon {
    display: inline-block;
    width: 2rem;
    height: 2rem;
    font-size: 1.6rem;
    color: $cBlack;
  }

  &__search {
    position: fixed;
    top: 8.6rem;
    left: 0;
    width: 100%;
    padding: 6rem 0;
    background: $cWhite;
    z-index: 2;
    max-height: 0;
    opacity: 0;
    pointer-events: none;
    transition: opacity $tr, max-height $tr;

    &--active {
      max-height: 100%;
      opacity: 1;
      pointer-events: all;
    }

    @include rwd('tablet') {
      padding-top: 0;
    }

    @include rwd('large-phone') {
      top: 10rem;
    }
  }

  &__searchContainer {
    border-bottom: 1px solid $cAddLight;
    margin: 3rem 0;
    display: flex;
    align-items: center;
    width: 100%;
    z-index: 1;

    @include rwd('small-tablet') {
      margin: 2rem 0;
    }
  }

  &__input {
    font-size: 1.8rem;
    margin-left: 1.5rem;
    color: $cAddDark02;
    width: 100%;
    height: 100%;
    padding: 3rem 0;
  }

  &__iconSearch {
    color: $cMain;
    font-size: 1.6rem;
    cursor: pointer;
    transition: trasnform $tr;
    width: 40px;
    height: 40px;
    display: flex;
    align-items: center;
    justify-content: center;

    &:hover span {
      transform: scale(1.05);
    }
  }

  &__iconClose {
    margin-left: auto;
    margin-right: -1rem;
    padding-right: 2rem;
    font-size: 1.8rem;
    border-left: 1px solid $cAddLight;
    padding-left: 2rem;
    height: 4.2rem;
    display: flex;
    align-items: center;
    cursor: pointer;
    width: 40px;
    height: 40px;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  &__dropdown {
    position: fixed;
    top: 0;
    left: 50%;
    transform: translateX(-50%);
    z-index: 2;
    opacity: 0;
    height: 0;
    pointer-events: none;
    transition: opacity $tr;

    &--active {
      top: 26rem;
      max-height: 100%;
      opacity: 1;
      pointer-events: all;
      height: auto;

      @include rwd('tablet') {
        top: 21rem;
      }
    }
  }
}
</style>
